import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { X, AlertTriangle, FileText, Calendar, HardDrive, Search } from "lucide-react";
import { BankDetailsForm } from "./BankDetailsForm.jsx";
import { DocumentsTab } from "./DocumentsTab.jsx";
import { FamilyDetailsForm } from "./FamilyDetailsForm.jsx";
import { OrgDetailsForm } from "./OrgDetailsForm.jsx";
import { PersonalDetailsForm } from "./PersonalDetailsForm.jsx";
import { ProfessionalDetailsForm } from "./ProfessionalDetailsForm.jsx";
import { ProofDocumentModal } from "./ProofDocumentModal.jsx";
import { QualificationDetailsForm } from "./QualificationDetailsForm.jsx";
import { StatutoryDetailsForm } from "./StatutoryDetailsForm.jsx";
import { Tabs } from "./Tabs.jsx";
import SearchableSelect from "../SearchableSelect.jsx";
import {
  employeeService,
  branchService,
  categoryService,
  departmentService,
  designationService,
  gradesService,
  reasonsService,
  subDepartmentService,
} from "../../../services/api";
import toast from "react-hot-toast";
import { usePermissions } from "../../../rbac/PermissionsContext";
import {
  formatDate,
} from "../../../lib/employeeHelpers";
import {
  validateOrgDetails,
  validatePersonalDetails,
  validateFamilyDetails,
  validateBankDetails,
  validateEducationalDetails,
  validateProfessionalDetails,
  validateStatutoryDetails,
  validateDocuments,
  hasFamilySectionData,
  hasEducationalSectionData,
  hasProfessionalSectionData,
} from "./validationHelpers";

const PROFILE_TAB_SCREEN_KEYS = {
  org: "org_details",
  personal: "personal_details",
  family: "family_details",
  bank: "bank_details",
  educational: "education_details",
  professional: "profession_details",
  statutory: "statutory_details",
  documents: "profile_documents",
};


export default function EmployeeProfileDetails({
  employee,
  darkMode,
  onCancel,
  enableESS = true,
  enableESSByDefault = false,
  isEmployeeView = false,
  showFooterButtons = true,
  forceReadOnly = false,
  onNavigateToTransfer,
  isRejoinPrefill = false,
  // Rejoin (Add New from relieved employee): only in Add New form
  isRejoin = false,
  setIsRejoin,
  rejoinSelectedEmployeeId,
  setRejoinSelectedEmployeeId,
  rejoinEmployeeList = [],
  rejoinListLoading = false,
  rejoinFetchingEmployee = false,
  onRejoinSearch,
  onRejoinSelectEmployee,
  onOnboardSuccess,
}) {
  // Centralized state for all employee data
  const [employeeData, setEmployeeData] = useState(employee || {});
  const [activeTab, setActiveTab] = useState("org");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editModeByTab, setEditModeByTab] = useState({});
  const [documentReplaceModal, setDocumentReplaceModal] = useState({
    isOpen: false,
    documentName: '',
    existingDoc: null,
    pendingDocumentData: null,
  });
  // const [feedback, setFeedback] = useState({}); // { tab: { type: 'success'|'error', message: '' } }

  // Refs to store form save handlers
  const formSaveHandlersRef = useRef({});
  /** Prevents parallel proof-document uploads (double-click / race). */
  const employeeDocumentUploadInFlightRef = useRef(false);
  const [documentsFinalizeState, setDocumentsFinalizeState] = useState({
    canFinalize: false,
  });
  const [rejoinSearchInput, setRejoinSearchInput] = useState("");
  const { hasScreenAccess, isAdmin, roleCode, snapshot } = usePermissions();

  // Reset employeeData when employee prop changes (including rejoin prefill set/clear)
  useEffect(() => {
    const base = {
      ...(employee || {}),
      persistedEsiApplicable: employee?.esiApplicable === true,
    };

    if (employee?.id) {
      // Edit employee: Send Invite is always unchecked (and stays false in DB)
      base.inviteSent = false;
    }
    if (employee?.user?.lastLoginAt) {
      // If they have already logged in, Send Invite should be false
      base.inviteSent = false;
    }
    if (!employee?.id) {
      // New employee (or rejoin): if ESS is enabled by default, check both Enable ESS and Send Invite
      if (enableESSByDefault) {
        if (base.essEnabled === undefined) base.essEnabled = true;
        if (base.inviteSent === undefined) base.inviteSent = true;
      } else {
        if (base.essEnabled === undefined) base.essEnabled = false;
        if (base.inviteSent === undefined) base.inviteSent = false;
      }
    }
    setEmployeeData(base);
    setActiveTab("org"); // Reset to first tab
  }, [employee?.id, isRejoinPrefill, enableESSByDefault]); // Reset when employee ID changes or rejoin prefill is set/cleared

  // If company-level ESS is disabled, ensure employee-level ESS + invite are off
  useEffect(() => {
    if (enableESS) return;
    setEmployeeData((prev) => {
      if (!prev) return prev;
      if (prev.essEnabled === false && prev.inviteSent === false) return prev;
      return { ...prev, essEnabled: false, inviteSent: false };
    });
  }, [enableESS]);

  // Function to trigger current tab's save - matches original button functionality
  const triggerFormSave = async () => {
    // First try to use exposed save handler (for forms that expose it)
    const saveHandler = formSaveHandlersRef.current[activeTab];
    if (saveHandler && typeof saveHandler === "function") {
      try {
        const result = await saveHandler();
        if (result && result.warning === "MASTER_DETAILS_WARNING") {
          return false; // Soft warning; keep popup open
        }
        if (result === false || result?.saved === false) {
          return false;
        }
        return result && result.created ? { success: true, created: true } : true; // Success; pass through created for onboard
      } catch (error) {
        // If the form raised a soft-warning (like master details), don't log as an error
        if (
          error?.code === "MASTER_DETAILS_WARNING" ||
          error?.message === "MASTER_DETAILS_WARNING"
        ) {
          return false; // Treat as non-success so footer won't close
        }
        // If exposed handler fails, fall back to button click
        console.error("Save handler error:", error);
        return false; // Failed
      }
    }

    // Fallback: find and click the form's save/next button programmatically
    // This matches the original functionality where buttons directly call handleSave
    const formRef = document.querySelector(`[data-tab="${activeTab}"]`);
    if (formRef) {
      // Find all buttons in the form (even if hidden)
      const buttons = Array.from(formRef.querySelectorAll("button"));

      // Look for save/update/next/onboard button (but not cancel/previous/close)
      let saveButton = null;

      for (const btn of buttons) {
        const text = (btn.textContent || "").toLowerCase().trim();
        const buttonType = btn.getAttribute("type");

        // Skip cancel, previous, close buttons
        if (
          text.includes("cancel") ||
          text.includes("previous") ||
          text.includes("close") ||
          (buttonType === "button" && text === "")
        ) {
          continue;
        }

        // Find the save/update/next/onboard button
        // This button directly calls handleSave which has all the validation and navigation logic
        if (
          text.includes("next") ||
          text.includes("save") ||
          text.includes("update") ||
          text.includes("onboard") ||
          text.includes("complete")
        ) {
          saveButton = btn;
          break;
        }
        console.warn(saveButton);
      }

      if (saveButton && !saveButton.disabled) {
        // Click the button - this will trigger handleSave which has all original functionality
        saveButton.click();
        // Return after a brief delay but do NOT assume success.
        // The inner form will handle navigation/toasts; we return false so callers don't prematurely close popups.
        await new Promise((resolve) => setTimeout(resolve, 100));
        return false;
      }

      // If we found the save button but it is disabled, do NOT bypass validation by calling handleSaveSection.
      // This prevents the footer button from saving incomplete forms (especially on Documents tab).
      if (saveButton && saveButton.disabled) {
        toast.error("Please complete all required fields before saving.", {
          position: "top-right",
        });
        return false;
      }
    }

    // Last resort: This shouldn't happen, but call handleSaveSection directly
    console.warn(
      "Could not find form save button, calling handleSaveSection directly"
    );
    try {
      await handleSaveSection(activeTab, employeeData);
      return true;
    } catch (error) {
      return false;
    }
  };

  // Master data state - fetched once and cached
  const [masterData, setMasterData] = useState({
    branches: [],
    departments: [],
    designations: [],
    subDepartments: [],
    grades: [],
    categories: [],
    resignationReasons: [],
    terminationReasons: [],
    reportingEmployees: [],
    totalEmployees: 0,
  });
  const [masterDataLoading, setMasterDataLoading] = useState(false);

  // Fetch all master data once when component mounts
  useEffect(() => {
    const fetchMasterData = async () => {
      setMasterDataLoading(true);
      try {
        console.log("Fetching master data for EmployeeProfileDetails...");

        // Fetch all master data in parallel
        const [
          branchesResponse,
          departmentsResponse,
          designationsResponse,
          subDepartmentsResponse,
          gradesResponse,
          categoriesResponse,
          resignationReasonsResponse,
          terminationReasonsResponse,
          employeesResponse,
        ] = await Promise.all([
          branchService.getBranches(),
          departmentService.getDepartments(),
          designationService.getDesignations(),
          subDepartmentService.getAllSubDepartments(),
          gradesService.getGrades(),
          categoryService.getAllCategories(),
          reasonsService.getAllResignationReasons(),
          reasonsService.getAllTerminationReasons(),
          employeeService.getEmployees(),
        ]);

        // Update master data state
        setMasterData((prev) => ({
          ...prev,
          branches: branchesResponse.success ? branchesResponse.data : [],
          departments: departmentsResponse.success
            ? departmentsResponse.data
            : [],
          designations: designationsResponse.success
            ? designationsResponse.data
            : [],
          subDepartments: subDepartmentsResponse.success
            ? subDepartmentsResponse.data
            : [],
          grades: gradesResponse.success ? gradesResponse.data : [],
          categories: categoriesResponse.success ? categoriesResponse.data : [],
          resignationReasons: resignationReasonsResponse.success
            ? resignationReasonsResponse.data
            : [],
          terminationReasons: terminationReasonsResponse.success
            ? terminationReasonsResponse.data
            : [],
          totalEmployees: employeesResponse.success
            ? employeesResponse.data.length
            : 0,
        }));

        console.log("Master data fetched successfully:", {
          branches: branchesResponse.success ? branchesResponse.data.length : 0,
          departments: departmentsResponse.success
            ? departmentsResponse.data.length
            : 0,
          designations: designationsResponse.success
            ? designationsResponse.data.length
            : 0,
          subDepartments: subDepartmentsResponse.success
            ? subDepartmentsResponse.data.length
            : 0,
          grades: gradesResponse.success ? gradesResponse.data.length : 0,
          categories: categoriesResponse.success
            ? categoriesResponse.data.length
            : 0,
          resignationReasons: resignationReasonsResponse.success
            ? resignationReasonsResponse.data.length
            : 0,
          terminationReasons: terminationReasonsResponse.success
            ? terminationReasonsResponse.data.length
            : 0,
          totalEmployees: employeesResponse.success
            ? employeesResponse.data.length
            : 0,
        });
      } catch (error) {
        console.error("Error fetching master data:", error);
        toast.error("Failed to load master data");
      } finally {
        setMasterDataLoading(false);
      }
    };

    fetchMasterData();
  }, []); // Only run once on mount

  // Tab order for navigation - restrict to only org and personal for employee view
  const tabOrder = [
    "org",
    "personal",
    "family",
    "bank",
    "educational",
    "professional",
    "statutory",
    "documents",
  ];

  const shouldApplyProfileRoleRules = isEmployeeView && !isAdmin;

  const canViewProfileTab = (tabId) => {
    if (!shouldApplyProfileRoleRules) return true;
    const screenKey = PROFILE_TAB_SCREEN_KEYS[tabId];
    if (!screenKey) return true;
    return hasScreenAccess("profile", screenKey, "view");
  };

  const canEditProfileTab = (tabId) => {
    if (!shouldApplyProfileRoleRules) return true;
    const screenKey = PROFILE_TAB_SCREEN_KEYS[tabId];
    if (!screenKey) return true;
    return (
      hasScreenAccess("profile", screenKey, "view") &&
      hasScreenAccess("profile", screenKey, "edit")
    );
  };

  const permissionAllowedTabs = useMemo(() => {
    if (!shouldApplyProfileRoleRules) return tabOrder;
    return tabOrder.filter((tabId) => {
      const screenKey = PROFILE_TAB_SCREEN_KEYS[tabId];
      if (!screenKey) return true;
      return hasScreenAccess("profile", screenKey, "view");
    });
  }, [shouldApplyProfileRoleRules, hasScreenAccess, snapshot, tabOrder]);

  const hasAnyProfileTabAccess = permissionAllowedTabs.length > 0;
  const activeTabPermissionReadOnly =
    shouldApplyProfileRoleRules && !canEditProfileTab(activeTab);

  useEffect(() => {
    if (!shouldApplyProfileRoleRules) return;
    if (!permissionAllowedTabs.includes(activeTab)) {
      setActiveTab(permissionAllowedTabs[0] || "org");
    }
  }, [activeTab, permissionAllowedTabs, shouldApplyProfileRoleRules]);

  // Navigation functions
  const goToNextTab = () => {
    const order = shouldApplyProfileRoleRules ? permissionAllowedTabs : tabOrder;
    const currentIndex = order.indexOf(activeTab);
    if (currentIndex >= 0 && currentIndex < order.length - 1) {
      setActiveTab(order[currentIndex + 1]);
    }
  };

  const goToPreviousTab = () => {
    const order = shouldApplyProfileRoleRules ? permissionAllowedTabs : tabOrder;
    const currentIndex = order.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(order[currentIndex - 1]);
    }
  };

  const isFirstTab = () => {
    const order = shouldApplyProfileRoleRules ? permissionAllowedTabs : tabOrder;
    return order.length > 0 && order[0] === activeTab;
  };
  const isLastTab = () => {
    const order = shouldApplyProfileRoleRules ? permissionAllowedTabs : tabOrder;
    return order.length > 0 && activeTab === order[order.length - 1];
  };

  // Check if this is a new employee (no ID yet)
  const isNewEmployee = !employeeData.id;

  // In admin flow (not employee view), "Update" should NOT auto-advance tabs.
  // We only allow auto-advance during onboarding (new employee) and in employee view flows.
  const shouldAutoAdvanceTab = isEmployeeView || isNewEmployee;
  const onNextAfterSave = shouldAutoAdvanceTab ? goToNextTab : () => {};

  // Start tabs in read-only mode until user clicks "Edit"
  // Exception: in employee view, Org tab should NOT show Edit/lock logic.
  useEffect(() => {
    if (!isNewEmployee && !(isEmployeeView && activeTab === "org")) {
      setEditModeByTab((prev) => ({ ...prev, [activeTab]: false }));
    }
  }, [activeTab, isNewEmployee, isEmployeeView, forceReadOnly]);

  const isActiveTabLocked = forceReadOnly
    ? true
    : (!isNewEmployee &&
        !editModeByTab[activeTab] &&
        !(isEmployeeView && activeTab === "org"));

  // Check if a section is filled/completed using proper validation logic
  const isSectionFilled = (section) => {
    // Use the actual validation functions that replicate the form validation logic
    
    switch (section) {
      case 'org':
        return validateOrgDetails(employeeData);
      
      case 'personal':
        return validatePersonalDetails(employeeData);
      
      case 'family':
        return validateFamilyDetails(employeeData);
      
      case 'bank':
        return validateBankDetails(employeeData);
      
      case 'educational':
        return validateEducationalDetails(employeeData);
      
      case 'professional':
        return validateProfessionalDetails(employeeData);
      
      case 'statutory':
        return validateStatutoryDetails(employeeData);
      
      case 'documents':
        return validateDocuments(employeeData);
      
      default:
        return false;
    }
  };

  // Get available tabs for employee based on filled sections (no strict sequence)
  const getAvailableTabsForEmployee = () => {
    // Always allow Org tab
    const availableTabs = ['org'];

    // For employee login we don't enforce a sequence.
    // Each tab is independently enabled only if its section is filled.
    const sectionIds = [
      'personal',
      'family',
      'bank',
      'educational',
      'professional',
      'statutory',
      'documents',
    ];

    const validationSnapshot = {};
    sectionIds.forEach((section) => {
      validationSnapshot[section] = isSectionFilled(section);
    });

    console.log('Validation results (employee view):', {
      org: isSectionFilled('org'),
      ...validationSnapshot,
    });

    // Add non-org tabs if section passes validation OR has data (ESS: enable when data exists even if flags like isOrphan/lessThanPrimary/isFresher are not set)
    if (validationSnapshot.personal) availableTabs.push('personal');
    if (validationSnapshot.family || hasFamilySectionData(employeeData)) availableTabs.push('family');
    if (validationSnapshot.bank) availableTabs.push('bank');
    if (validationSnapshot.educational || hasEducationalSectionData(employeeData)) availableTabs.push('educational');
    if (validationSnapshot.professional || hasProfessionalSectionData(employeeData)) availableTabs.push('professional');

    // Statutory only if applicable and filled
    if (
      (employeeData.pfApplicable || employeeData.esiApplicable) &&
      validationSnapshot.statutory
    ) {
      availableTabs.push('statutory');
    }

    if (validationSnapshot.documents) availableTabs.push('documents');

    return availableTabs;
  };

  // Restrict tab navigation for new employees (org first). For ESS employees, RBAC view only.
  const canNavigateToTab = (tabName) => {
    if (isRejoinPrefill) return true;
    if (isNewEmployee) {
      return tabName === "org";
    }
    if (isEmployeeView && shouldApplyProfileRoleRules) {
      const screenKey = PROFILE_TAB_SCREEN_KEYS[tabName];
      if (!screenKey) return true;
      return hasScreenAccess("profile", screenKey, "view");
    }
    if (isEmployeeView) {
      const availableTabs = getAvailableTabsForEmployee();
      return availableTabs.includes(tabName);
    }
    return true;
  };

  // Override tab navigation for new employees
  const handleTabChange = (tabName) => {
    if (canNavigateToTab(tabName)) {
      setActiveTab(tabName);
    }
  };

  // Update employeeData when employee prop changes
  useEffect(() => {
    if (employee) {
      // Transform the API response to match our form structure
      // const transformedEmployee = {
      //     // Basic employee fields
      //     id: employee.id,
      //     employeeId: employee.employeeId,
      //     userId: employee.userId,
      //     departmentId: employee.departmentId,
      //     designationId: employee.designationId,
      //     branchId: employee.branchId,
      //     subDepartmentId: employee.subDepartmentId,
      //     gradeId: employee.gradeId,
      //     categoryId: employee.categoryId,
      //     reportingManagerId: employee.reportingManagerId,
      //     joiningDate: employee.joiningDate,
      //     employmentStatus: employee.employmentStatus,
      //     employmentType: employee.employmentType,
      //     photo: employee.photo,

      //     // User details (nested)
      //     user: employee.user || {},

      //     // Department, designation, branch details (nested objects)
      //     department: employee.department || {},
      //     designation: employee.designation || {},
      //     branch: employee.branch || {},
      //     reportingManager: employee.reportingManager || {},

      //     // Address details (nested)
      //     address: employee.address || {},

      //     // Arrays
      //     familyMembers: employee.familyMembers || [],
      //     qualifications: employee.qualifications || [],
      //     experiences: employee.experiences || [],
      //     documents: employee.documents || [],

      //     // Bank details (nested)
      //     bankDetails: employee.bankDetails || {},

      //     // Map address fields to form structure (from address object)
      //     presentHouseNumber: employee.address?.address || '',
      //     presentCity: employee.address?.city || '',
      //     presentState: employee.address?.state || '',
      //     presentCountry: employee.address?.country || 'India',
      //     presentPincode: employee.address?.pincode || '',

      //     permanentHouseNumber: employee.address?.permanentAddress || '',
      //     permanentCity: employee.address?.permanentCity || '',
      //     permanentState: employee.address?.permanentState || '',
      //     permanentCountry: employee.address?.permanentCountry || 'India',
      //     permanentPincode: employee.address?.permanentPincode || '',

      //     // Emergency contact (if available in the response)
      //     emergencyContact: employee.emergencyContact || { name: '', number: '', relationship: '' },

      //     // Bank details mapping (from employee object)
      //     bankName: employee.bankName || '',
      //     accountNumber: employee.accountNumber || '',
      //     ifscCode: employee.ifscCode || '',
      //     branch: employee.branch || '',

      //     // Additional fields from API response
      //     aadharNumber: employee.aadharNumber || '',
      //     panNumber: employee.panNumber || '',
      //     email: employee.email || employee.user?.email || '',
      //     inviteSent: employee.inviteSent || false,
      //     confirmationDate: employee.confirmationDate || '',
      //     resignationDate: employee.resignationDate || '',
      //     relievedDate: employee.relievedDate || '',
      //     reason: employee.reason || '',

      //     // Checkbox fields
      //     isOrphan: employee.isOrphan || false,
      //     lessThanPrimary: employee.lessThanPrimary || false,
      //     isFresher: employee.isFresher || false,
      // };

      console.log("Employee data:", employee);
      setEmployeeData({
        ...employee,
        persistedEsiApplicable:
          employee?.esiApplicable === true ||
          employee?.esiApplicable === 1 ||
          String(employee?.esiApplicable || "").trim().toLowerCase() === "true" ||
          String(employee?.esiApplicable || "").trim() === "1",
      });
    }
  }, [employee]);

  // Save handler for each tab
  const handleSaveSection = async (section, sectionData) => {
    setLoading(true);
    // setFeedback({});
    try {
      console.log(`Saving ${section} section:`, sectionData);
      let response;
      let responseData;
      // If Org tab and no employeeId, create
      if (section === "org" && !employeeData.id) {
        response = await employeeService.createEmployee(sectionData);
        const payload = response?.data;
        responseData = payload?.data ?? payload;
        const createdId = responseData?.id;
        if (responseData?.inviteEmailFailed) {
          toast.error(responseData.inviteEmailFailedMessage || "Failed to send welcome email. Send Invite has been unchecked.");
        }
        const inviteSentFromCreate = responseData?.inviteSent ?? false;
        const welcomeEmailSentFromCreate = responseData?.welcomeEmailSent ?? false;
        setEmployeeData((prev) => ({
          ...prev,
          ...responseData,
          inviteSent: inviteSentFromCreate,
          welcomeEmailSent: welcomeEmailSentFromCreate,
          persistedEsiApplicable:
            responseData?.esiApplicable === true ||
            responseData?.esiApplicable === 1 ||
            String(responseData?.esiApplicable || "").trim().toLowerCase() === "true" ||
            String(responseData?.esiApplicable || "").trim() === "1",
        }));
        // Fetch full employee so personal email, photo, family images are in state and cache (rejoin or normal)
        if (createdId) {
          try {
            const fullRes = await employeeService.getEmployee(createdId);
            const fullData = fullRes?.data ?? fullRes;
            if (fullData && typeof fullData.id !== "undefined") {
              setEmployeeData((prev) => ({
                ...prev,
                ...fullData,
                user: fullData.user ?? prev?.user,
                familyMembers: fullData.familyMembers ?? prev?.familyMembers,
                address: fullData.address ?? prev?.address,
                documents: fullData.documents ?? prev?.documents,
                inviteSent: inviteSentFromCreate,
                welcomeEmailSent: welcomeEmailSentFromCreate,
                persistedEsiApplicable:
                  fullData?.esiApplicable === true ||
                  fullData?.esiApplicable === 1 ||
                  String(fullData?.esiApplicable || "").trim().toLowerCase() === "true" ||
                  String(fullData?.esiApplicable || "").trim() === "1",
              }));
            }
          } catch (e) {
            console.warn("Post-create getEmployee for cache/display:", e);
          }
        }
        // setFeedback({ [section]: { type: 'success', message: 'Employee onboarded successfully.' } });
      } else {
        // Update: send sectionData + which tab we're saving from (so backend only sends org-details email when saving from Org tab)
        response = await employeeService.updateEmployee(
          employeeData.id,
          { ...sectionData, updateSection: section }
        );
        console.log("Backend response:", response);

        const payload = response?.data;
        responseData = payload?.data ?? payload;

        // Handle different sections appropriately
        if (section === "personal") {
          // For personal section, we need to preserve the address object structure
          // and only update the specific fields that were changed
          setEmployeeData((prev) => {
            const updatedData = { ...prev };

            // Update emergency contact fields
            if (sectionData.emergencyContactName !== undefined) {
              updatedData.emergencyContactName =
                sectionData.emergencyContactName;
            }
            if (sectionData.emergencyContactPhone !== undefined) {
              updatedData.emergencyContactPhone =
                sectionData.emergencyContactPhone;
            }
            if (sectionData.emergencyContactRelation !== undefined) {
              updatedData.emergencyContactRelation =
                sectionData.emergencyContactRelation;
            }

            // Update photo if provided
            if (sectionData.photo !== undefined) {
              updatedData.photo = sectionData.photo;
            }

            // Update father name / marital status if provided
            if (sectionData.fatherName !== undefined) {
              updatedData.fatherName = sectionData.fatherName;
            }
            if (sectionData.maritalStatus !== undefined) {
              updatedData.maritalStatus = sectionData.maritalStatus;
            }

            // Update address fields in the address object
            if (
              sectionData.address !== undefined ||
              sectionData.city !== undefined ||
              sectionData.state !== undefined ||
              sectionData.country !== undefined ||
              sectionData.pincode !== undefined ||
              sectionData.permanentAddress !== undefined ||
              sectionData.permanentCity !== undefined ||
              sectionData.permanentState !== undefined ||
              sectionData.permanentCountry !== undefined ||
              sectionData.permanentPincode !== undefined
            ) {
              updatedData.address = {
                ...prev.address,
                address:
                  sectionData.address !== undefined
                    ? sectionData.address
                    : prev.address?.address,
                city:
                  sectionData.city !== undefined
                    ? sectionData.city
                    : prev.address?.city,
                state:
                  sectionData.state !== undefined
                    ? sectionData.state
                    : prev.address?.state,
                country:
                  sectionData.country !== undefined
                    ? sectionData.country
                    : prev.address?.country,
                pincode:
                  sectionData.pincode !== undefined
                    ? sectionData.pincode
                    : prev.address?.pincode,
                permanentAddress:
                  sectionData.permanentAddress !== undefined
                    ? sectionData.permanentAddress
                    : prev.address?.permanentAddress,
                permanentCity:
                  sectionData.permanentCity !== undefined
                    ? sectionData.permanentCity
                    : prev.address?.permanentCity,
                permanentState:
                  sectionData.permanentState !== undefined
                    ? sectionData.permanentState
                    : prev.address?.permanentState,
                permanentCountry:
                  sectionData.permanentCountry !== undefined
                    ? sectionData.permanentCountry
                    : prev.address?.permanentCountry,
                permanentPincode:
                  sectionData.permanentPincode !== undefined
                    ? sectionData.permanentPincode
                    : prev.address?.permanentPincode,
              };
            }

            return updatedData;
          });
        } else if (section === "org") {
          if (responseData && responseData.inviteEmailFailed) {
            toast.error(responseData.inviteEmailFailedMessage || "Failed to send welcome email. Send Invite has been unchecked.");
          }
          setEmployeeData((prev) => {
            const next = { ...prev, ...sectionData };
            if (responseData && typeof responseData === "object") {
              if (Object.prototype.hasOwnProperty.call(responseData, "essEnabled")) {
                next.essEnabled = !!responseData.essEnabled;
              }
              if (Object.prototype.hasOwnProperty.call(responseData, "inviteSent")) {
                next.inviteSent = !!responseData.inviteSent;
              }
              if (Object.prototype.hasOwnProperty.call(responseData, "welcomeEmailSent")) {
                next.welcomeEmailSent = !!responseData.welcomeEmailSent;
              }
              if (responseData.user && typeof responseData.user === "object") {
                next.user = { ...prev.user, ...responseData.user };
              }
            }
            return next;
          });
        } else if (section === "family") {
          setEmployeeData((prev) => {
            const normalizedMembers = Array.isArray(responseData?.familyMembers)
              ? responseData.familyMembers.map((m) => ({
                  id: m.id,
                  name: m.name || "",
                  dateOfBirth: m.date_of_birth || m.dateOfBirth || "",
                  relationship: m.relationship || "",
                  gender: m.gender || "",
                  phone: m.mobile_number || m.phone || "",
                  aadhaarNumber: m.aadhaar_number || m.aadhaarNumber || "",
                  nominee: m.nominee || m.nominee_status || false,
                  sharePercentage: m.share_percentage || m.sharePercentage || 0,
                  dependent: m.dependent || m.dependent_status || false,
                  photo: m.photo || m.photo_path || null,
                  photoFileName: m.photo_file_name || m.photoFileName || "",
                  photoChanged: false,
                  user_id: m.user_id || prev.userId || prev.user?.id,
                  created_at: m.created_at,
                  updated_at: m.updated_at,
                }))
              : prev.familyMembers;

            const next = {
              ...prev,
              isOrphan:
                responseData && Object.prototype.hasOwnProperty.call(responseData, "isOrphan")
                  ? responseData.isOrphan
                  : prev.isOrphan,
              familyMembers: normalizedMembers,
            };
            return next;
          });
        } else {
          // For other sections, use the original logic
          setEmployeeData((prev) => {
            const next = { ...prev, ...sectionData };

            // Documents tab sends metadata-only payload (no filePath) for saving.
            // Never let that overwrite the already-uploaded documents list in state,
            // otherwise admin validation will incorrectly think uploads are missing.
            if (section === "documents" && Object.prototype.hasOwnProperty.call(sectionData, "documents")) {
              next.documents = prev.documents;
            }

            // IMPORTANT: persistedEsiApplicable should reflect backend-persisted state only.
            // Do not update it from unsaved UI toggles.
            if (Object.prototype.hasOwnProperty.call(sectionData, "esiApplicable")) {
              if (responseData && Object.prototype.hasOwnProperty.call(responseData, "esiApplicable")) {
                next.persistedEsiApplicable =
                  responseData.esiApplicable === true ||
                  responseData.esiApplicable === 1 ||
                  String(responseData.esiApplicable || "").trim().toLowerCase() === "true" ||
                  String(responseData.esiApplicable || "").trim() === "1";
              }
            }
            return next;
          });
        }
        // toast.success('Section updated successfully.');
        // setFeedback({ [section]: { type: 'success', message: 'Section updated successfully.' } });
      }

      return responseData;
    } catch (err) {
      console.error("Save error:", err);
      console.error("Error structure:", {
        message: err.message,
        response: err.response,
        errors: err.errors,
        data: err.data,
      });

      // Check if this is a validation error (400 status) that should be handled by the form
      if (err.response?.status === 400 && err.response?.data?.errors) {
        console.log("Validation errors found:", err.response.data.errors);
        // For validation errors, let the form component handle them
        // Don't set feedback, let the error bubble up to the form
        throw err; // Re-throw so the form can handle it
      } else if (err.message === "Validation failed" && err.errors) {
        console.log("Validation errors in err.errors:", err.errors);
        // Handle case where error is thrown by handleApiError but still has errors
        const validationError = new Error("Validation failed");
        validationError.response = {
          status: 400,
          data: { errors: err.errors },
        };
        throw validationError;
      } else if (err.message === "Validation failed" && err.data?.errors) {
        console.log("Validation errors in err.data.errors:", err.data.errors);
        // Handle case where error data is in err.data
        const validationError = new Error("Validation failed");
        validationError.response = {
          status: 400,
          data: { errors: err.data.errors },
        };
        throw validationError;
      } else {
        // For other errors (500, network, etc.), show feedback
        // setFeedback({ [section]: { type: 'error', message: err.message || 'Failed to save.' } });

        // IMPORTANT: rethrow so calling forms do not navigate to the next tab on failure
        throw err;
      }
    } finally {
      setLoading(false);
    }
  };

  // Document upload handler (for Documents tab) - Hybrid approach
  const handleDocumentUpload = async (documentData) => {
    const normalizeDocumentName = (value) => {
      const v = (value || "").trim();
      if (!v) return "";
      const aliasMap = {
        "Pan & Aadhaar Linked Proof": "PAN & Aadhaar Linked",
        "Proof of Bank Account": "Bank Proof",
      };
      return aliasMap[v] || v;
    };

    const docs = employeeData.documents || [];
    const canonicalName = normalizeDocumentName(documentData.documentName);
    const docKey = (doc) =>
      normalizeDocumentName(doc.documentName || doc.document_name || doc.type);
    const existingDoc = docs.find((doc) => docKey(doc) === canonicalName);

    if (existingDoc) {
      setDocumentReplaceModal({
        isOpen: true,
        documentName: documentData.documentName,
        existingDoc: existingDoc,
        pendingDocumentData: documentData,
      });
      return;
    }

    await processDocumentUpload(documentData);
  };

  // Separate function to process document upload
  const processDocumentUpload = async (documentData) => {
    if (employeeDocumentUploadInFlightRef.current) {
      toast.error("An upload is already in progress. Please wait.", {
        position: "top-right",
      });
      throw new Error("Upload already in progress");
    }
    employeeDocumentUploadInFlightRef.current = true;
    try {
      const normalizeDocumentName = (value) => {
        const v = (value || "").trim();
        if (!v) return "";
        const aliasMap = {
          "Pan & Aadhaar Linked Proof": "PAN & Aadhaar Linked",
          "Proof of Bank Account": "Bank Proof",
        };
        return aliasMap[v] || v;
      };

      const canonicalName = normalizeDocumentName(documentData.documentName);
      const docs = employeeData.documents || [];
      const docKey = (doc) =>
        normalizeDocumentName(doc.documentName || doc.document_name || doc.type);

      // If there's a file to upload, handle it
      if (documentData.file) {
        const formData = new FormData();
        formData.append("document", documentData.file);
        formData.append("documentName", canonicalName);
        formData.append("employeeId", employeeData.id);
        formData.append("comment", documentData.comment || "");
        // Always set replaceExisting to true since we're processing after confirmation
        formData.append("replaceExisting", "true");

        // Upload to backend
        const uploadResult = await employeeService.uploadDocument(formData);

        // Update documentData with the uploaded file info
        const serverDocId = uploadResult?.documentId ?? uploadResult?.id;
        const originalFileName = uploadResult?.originalFileName || documentData.file?.name;
        const downloadFileName = uploadResult?.downloadFileName || originalFileName;
        documentData.documentId = serverDocId;
        documentData.filePath = uploadResult?.filePath;
        documentData.originalFileName = originalFileName;
        documentData.downloadFileName = downloadFileName;
        // Keep a normalized filename for UI + validation
        documentData.fileName = downloadFileName;
        documentData.size = uploadResult?.size ?? documentData.file?.size;
        documentData.lastUpdated = new Date().toISOString();
      }

      // Update local state
      let updatedDocuments;
      const existingDocIndex = docs.findIndex(
        (doc) => docKey(doc) === canonicalName
      );

      if (existingDocIndex !== -1) {
        // Replace existing document in the list
        updatedDocuments = [...docs];
        updatedDocuments[existingDocIndex] = { ...documentData, documentName: canonicalName };
      } else {
        // Add new document to the list
        updatedDocuments = [...docs, { ...documentData, documentName: canonicalName }];
      }

      setEmployeeData((prev) => ({ ...prev, documents: updatedDocuments }));

      if (isEmployeeView && employeeData?.id) {
        try {
          const resp = await employeeService.getEmployee(employeeData.id);
          const data = resp?.data || resp;
          const refreshedDocs = data?.documents;
          if (Array.isArray(refreshedDocs)) {
            setEmployeeData((prev) => ({ ...prev, documents: refreshedDocs }));
          }
        } catch (e) {
          // ignore
        }
      }
    } catch (error) {
      console.error("Document upload error:", error);
      if (error?.message !== "Upload already in progress") {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to upload document. Please try again.";
        toast.error(msg, { position: "top-right" });
      }
      throw error;
    } finally {
      employeeDocumentUploadInFlightRef.current = false;
    }
  };

  // Handle document replacement confirmation
  const handleDocumentReplaceConfirm = async (confirmed) => {
    if (confirmed && documentReplaceModal.pendingDocumentData) {
      // Close modal first
      const pendingData = documentReplaceModal.pendingDocumentData;
      setDocumentReplaceModal({ isOpen: false, documentName: '', existingDoc: null, pendingDocumentData: null });
      // Process the upload
      await processDocumentUpload(pendingData);
    } else {
      // User cancelled
      setDocumentReplaceModal({ isOpen: false, documentName: '', existingDoc: null, pendingDocumentData: null });
    }
  };

  return (
    <div
      className={`flex h-full min-h-0 flex-col ${
        darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
      }`}
    >
      {/* Fixed Header Section - Tabs Only */}
      <div className={`flex-shrink-0 ${darkMode ? "bg-gray-800" : "bg-white"}`}>
        {/* Fixed Tabs Header */}
        <div>
          <Tabs
            activeTab={activeTab}
            setActiveTab={handleTabChange}
            isNewEmployee={isNewEmployee}
            isRejoinPrefill={isRejoinPrefill}
            onCloseForm={onCancel}
            isEmployeeView={isEmployeeView}
            employee={employeeData}
            availableTabs={
              isEmployeeView && isNewEmployee && !isRejoinPrefill
                ? getAvailableTabsForEmployee()
                : null
            }
            allowedByPermissionTabs={
              shouldApplyProfileRoleRules ? permissionAllowedTabs : null
            }
          />
        </div>
      </div>

      {/* Scrollable Content Area - Fixed height with internal scroll */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-6">
          {activeTab === "org" && canViewProfileTab("org") && (
            <div data-tab="org">
              {/* Rejoin: above employee code, below org details tab; dropdown visible only when checkbox is checked */}
              {!employeeData?.id && typeof setIsRejoin === "function" && (
                <div
                  className={`mb-4 flex flex-wrap items-center gap-4 border-b border-gray-300 pb-4 dark:border-gray-600 ${
                    darkMode ? "text-gray-100" : "text-gray-900"
                  }`}
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isRejoin}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setIsRejoin(checked);
                        if (!checked && typeof setRejoinSelectedEmployeeId === "function") {
                          setRejoinSelectedEmployeeId(null);
                          if (typeof onRejoinSelectEmployee === "function") {
                            onRejoinSelectEmployee(null);
                          }
                        }
                      }}
                      className="rounded border-gray-400"
                    />
                    <span className="text-sm font-medium">Rejoin</span>
                  </label> 
                  {isRejoin && (
                    <div className="flex flex-col gap-3 w-full max-w-[280px]">
                      <div className="w-full">
                        <SearchableSelect
                          id="rejoinEmployeeId"
                          label="Select relieved employee"
                          value={rejoinSelectedEmployeeId ?? ""}
                          onChange={(e) => {
                            const id = e.target.value ? Number(e.target.value) : null;
                            if (typeof setRejoinSelectedEmployeeId === "function") {
                              setRejoinSelectedEmployeeId(id);
                            }
                            if (typeof onRejoinSelectEmployee === "function") {
                              onRejoinSelectEmployee(id);
                            }
                          }}
                          staticOptions={(rejoinEmployeeList || []).map((emp) => {
                            const code = emp.empCode || emp.employeeCode || "";
                            const name = [emp.user?.name, emp.user?.lastName].filter(Boolean).join(" ").trim();
                            const label = [code, name].filter(Boolean).length
                              ? [code, name].filter(Boolean).join(" - ")
                              : `Employee ${emp.id}`;
                            const isSearchable = (v) => {
                              if (v == null || v === "") return false;
                              const s = String(v).trim().toLowerCase();
                              if (!s) return false;
                              if (s === "not available" || s === "n/a" || s === "na") return false;
                              return true;
                            };
                            const searchParts = [
                              code,
                              name,
                              emp.user?.email,
                              emp.email,
                              emp.user?.phone,
                              emp.aadharNumber,
                              emp.panNumber,
                            ].filter(isSearchable).map((s) => String(s).trim().toLowerCase());
                            const searchText = searchParts.join(" ");
                            return {
                              value: emp.id,
                              label,
                              searchText,
                            };
                          })}
                          filterOption={(option, inputValue) => {
                            if (!inputValue || !inputValue.trim()) return true;
                            const searchStr = (option.data?.searchText ?? option.searchText ?? option.label ?? "").toLowerCase();
                            const tokens = inputValue.trim().toLowerCase().split(/\s+/).filter(Boolean);
                            return tokens.every((t) => searchStr.includes(t));
                          }}
                          openMenuOnlyWhenTyping={true}
                          darkMode={!!darkMode}
                          disabled={rejoinListLoading || rejoinFetchingEmployee}
                          error={null}
                        />
                        {rejoinFetchingEmployee && (
                          <span className="text-xs text-gray-500 mt-1 block">Loading employee details...</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <OrgDetailsForm
                employeeData={employeeData}
                setEmployeeData={setEmployeeData}
                onSaveSuccess={() => {
                  if (!isNewEmployee && !forceReadOnly && !isEmployeeView) {
                    setEditModeByTab((prev) => ({ ...prev, org: false }));
                  }
                }}
                onSaveSection={(data) => handleSaveSection("org", data)}
                rejoinSelectedEmployeeId={rejoinSelectedEmployeeId}
                loading={loading}
                // feedback={feedback['org']}
                darkMode={darkMode}
                onCancel={onCancel}
                onNext={onNextAfterSave}
                isLastTab={isLastTab()}
                // Employee view: Org tab is always view-only but should not use Edit-lock UI.
                readOnly={isEmployeeView ? true : isActiveTabLocked || loading}
                isEmployeeView={isEmployeeView}
                masterData={masterData}
                masterDataLoading={masterDataLoading}
                onSaveTrigger={(saveFn) => {
                  if (saveFn && typeof saveFn === "function") {
                    formSaveHandlersRef.current["org"] = saveFn;
                  }
                }}
                onNavigateToTransfer={onNavigateToTransfer}
              />
            </div>
          )}
          {activeTab === "personal" &&
            canViewProfileTab("personal") &&
            (!isNewEmployee || isRejoinPrefill) && (
            <div data-tab="personal">
              <PersonalDetailsForm
                employeeData={employeeData}
                setEmployeeData={setEmployeeData}
                onSaveSection={(data) => handleSaveSection("personal", data)}
                loading={loading}
                // feedback={feedback['personal']}
                darkMode={darkMode}
                onCancel={onCancel}
                onNext={onNextAfterSave}
                isLastTab={isLastTab()}
                isEmployeeView={isEmployeeView}
                readOnly={isActiveTabLocked || !canEditProfileTab("personal")}
                hideButtons={showFooterButtons}
                readOnly={isActiveTabLocked || loading}
                onSaveTrigger={(saveFn) => {
                  if (saveFn && typeof saveFn === "function") {
                    formSaveHandlersRef.current["personal"] = saveFn;
                  }
                }}
              />
            </div>
          )}
          {activeTab === "family" &&
            canViewProfileTab("family") &&
            (!isNewEmployee || isRejoinPrefill) && (
            <div data-tab="family">
              <FamilyDetailsForm
                employeeData={employeeData}
                setEmployeeData={setEmployeeData}
                onSaveSection={(data) => handleSaveSection("family", data)}
                loading={loading}
                // feedback={feedback['family']}
                darkMode={darkMode}
                onCancel={onCancel}
                onNext={onNextAfterSave}
                isLastTab={isLastTab()}
                readOnly={isActiveTabLocked || !canEditProfileTab("family")}
                hideButtons={showFooterButtons}
                readOnly={isActiveTabLocked || loading}
                onSaveTrigger={(saveFn) => {
                  if (saveFn && typeof saveFn === "function") {
                    formSaveHandlersRef.current["family"] = saveFn;
                  }
                }}
              />
            </div>
          )}
          {activeTab === "bank" &&
            canViewProfileTab("bank") &&
            (!isNewEmployee || isRejoinPrefill) && (
            <div data-tab="bank">
              <BankDetailsForm
                employeeData={employeeData}
                setEmployeeData={setEmployeeData}
                onSaveSection={(data) => handleSaveSection("bank", data)}
                loading={loading}
                // feedback={feedback['bank']}
                darkMode={darkMode}
                onCancel={onCancel}
                onNext={onNextAfterSave}
                isLastTab={isLastTab()}
                readOnly={isActiveTabLocked || !canEditProfileTab("bank")}
                hideButtons={showFooterButtons}
                readOnly={isActiveTabLocked || loading}
                onSaveTrigger={(saveFn) => {
                  if (saveFn && typeof saveFn === "function") {
                    formSaveHandlersRef.current["bank"] = saveFn;
                  }
                }}
              />
            </div>
          )}
          {activeTab === "educational" &&
            canViewProfileTab("educational") &&
            (!isNewEmployee || isRejoinPrefill) && (
            <div data-tab="educational">
              <QualificationDetailsForm
                employeeData={employeeData}
                setEmployeeData={setEmployeeData}
                onSaveSection={(data) => handleSaveSection("educational", data)}
                loading={loading}
                // feedback={feedback['educational']}
                darkMode={darkMode}
                onCancel={onCancel}
                onNext={onNextAfterSave}
                isLastTab={isLastTab()}
                readOnly={isActiveTabLocked || !canEditProfileTab("educational")}
                hideButtons={showFooterButtons}
                readOnly={isActiveTabLocked || loading}
                onSaveTrigger={(saveFn) => {
                  if (saveFn && typeof saveFn === "function") {
                    formSaveHandlersRef.current["educational"] = saveFn;
                  }
                }}
              />
            </div>
          )}
          {activeTab === "professional" &&
            canViewProfileTab("professional") &&
            (!isNewEmployee || isRejoinPrefill) && (
            <div data-tab="professional">
              <ProfessionalDetailsForm
                employeeData={employeeData}
                setEmployeeData={setEmployeeData}
                onSaveSection={(data) => handleSaveSection("professional", data)}
                loading={loading}
                // feedback={feedback['professional']}
                darkMode={darkMode}
                onCancel={onCancel}
                onNext={onNextAfterSave}
                isLastTab={isLastTab()}
                readOnly={isActiveTabLocked || !canEditProfileTab("professional")}
                hideButtons={showFooterButtons}
                readOnly={isActiveTabLocked || loading}
                onSaveTrigger={(saveFn) => {
                  if (saveFn && typeof saveFn === "function") {
                    formSaveHandlersRef.current["professional"] = saveFn;
                  }
                }}
              />
            </div>
          )}
          {activeTab === "statutory" &&
            canViewProfileTab("statutory") &&
            (!isNewEmployee || isRejoinPrefill) && (
            <div data-tab="statutory">
              <StatutoryDetailsForm
                employeeData={employeeData}
                setEmployeeData={setEmployeeData}
                onSaveSection={(data) => handleSaveSection("statutory", data)}
                loading={loading}
                // feedback={feedback['statutory']}
                darkMode={darkMode}
                onCancel={onCancel}
                onNext={onNextAfterSave}
                isLastTab={isLastTab()}
                isEmployeeView={isEmployeeView}
                readOnly={isActiveTabLocked || !canEditProfileTab("statutory")}
                readOnly={isActiveTabLocked || loading}
                onSaveTrigger={(saveFn) => {
                  if (saveFn && typeof saveFn === "function") {
                    formSaveHandlersRef.current["statutory"] = saveFn;
                  }
                }}
              />
            </div>
          )}
          {activeTab === "documents" &&
            canViewProfileTab("documents") &&
            (!isNewEmployee || isRejoinPrefill) && (
            <div data-tab="documents">
              <DocumentsTab
                employeeData={employeeData}
                setEmployeeData={setEmployeeData}
                onSaveSection={(data) => handleSaveSection("documents", data)}
                loading={loading}
                // feedback={feedback['documents']}
                setIsUploadModalOpen={setIsUploadModalOpen}
                darkMode={darkMode}
                onCancel={onCancel}
                onNext={onNextAfterSave}
                isLastTab={isLastTab()}
                isEmployeeView={isEmployeeView}
                isPfApplicable={employeeData.pfApplicable}
                isEsiApplicable={employeeData.persistedEsiApplicable}
                totalEmployees={masterData.totalEmployees}
                readOnly={isActiveTabLocked || !canEditProfileTab("documents")}
                hideButtons={showFooterButtons}
                readOnly={isActiveTabLocked || loading}
                onFinalizeStateChange={(state) => setDocumentsFinalizeState(state)}
                onSaveTrigger={(saveFn) => {
                  if (saveFn && typeof saveFn === "function") {
                    formSaveHandlersRef.current["documents"] = saveFn;
                  }
                }}
              />
            </div>
          )}

          {/* Show message for new employees trying to access other tabs (not when rejoin prefill) */}
          {isNewEmployee && !isRejoinPrefill && activeTab !== "org" && (
            <div
              className={`p-8 text-center ${
                darkMode
                  ? "bg-gray-700 text-gray-300"
                  : "bg-gray-100 text-gray-600"
              } rounded-lg`}
            >
              <div className="mb-4">
                <svg
                  className="mx-auto h-12 w-12 text-amber-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium mb-2">
                Employee Must Be Onboarded First
              </h3>
              <p className="mb-4">
                Please complete the Organization Details tab and onboard the
                employee before accessing other sections.
              </p>
              <button
                onClick={() => setActiveTab("org")}
                className={`px-4 py-5 rounded-md ${
                  darkMode
                    ? "bg-redhalo-red hover:opacity-95 text-white"
                    : "bg-redhalo-mint/400 hover:rh-btn-primary"
                } transition-colors`}
              >
                Go to Organization Details
              </button>
            </div>
          )}

          {shouldApplyProfileRoleRules && !hasAnyProfileTabAccess && (
            <div className="p-8 text-center bg-gray-100 text-gray-600 rounded-lg">
              <h3 className="text-lg font-medium mb-2">Profile access not granted</h3>
              <p>
                This role does not have access to any profile tab. Ask an admin to
                grant access in User Roles - Screen Access.
              </p>
            </div>
          )}

          {/* Show message for employee users trying to access restricted tabs
                {isEmployeeView && activeTab !== 'org' && activeTab !== 'personal' && (
                    <div className={`p-8 text-center ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'} rounded-lg`}>
                        <div className="mb-4">
                            <svg className="mx-auto h-12 w-12 text-redhalo-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium mb-2">Access Restricted</h3>
                        <p className="mb-4">
                            As an employee, you can only access Organization Details and Personal Information sections.
                        </p>
                        <button
                            onClick={() => setActiveTab('org')}
                            className={`px-4 py-2 rounded-md ${darkMode ? 'bg-redhalo-red hover:opacity-95 text-white' : 'bg-redhalo-mint/400 hover:rh-btn-primary'} transition-colors`}
                        >
                            Go to Organization Details
                        </button>
                    </div>
                )} */}
        </div>
      </div>

      {/* Fixed Footer with Action Buttons - Bottom Right */}
      {showFooterButtons && (
        <div
          className={`flex-shrink-0 border-t ${
            darkMode
              ? "border-gray-600 bg-gray-800"
              : "border-gray-200 bg-white"
          } p-4`}
        >
          <div className="flex justify-between items-center">
            {/* Left side - ESS toggles (Org tab only) */}
            <div className="flex items-center space-x-4">
              {activeTab === "org" && !isEmployeeView && enableESS && (
                <>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="footerEssEnabled"
                      name="footerEssEnabled"
                      checked={employeeData.essEnabled || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setEmployeeData((prev) => ({
                          ...prev,
                          essEnabled: checked,
                          inviteSent: checked ? (prev?.inviteSent || false) : false,
                        }));
                      }}
                      className="mr-2 rounded border-0 text-redhalo-navy shadow-sm focus:ring focus:ring-redhalo-red/25 focus:ring-opacity-50"
                      disabled={isActiveTabLocked || loading}
                    />
                    <span
                      className={`text-sm font-medium ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Enable ESS for this employee
                    </span>
                  </label>

                  <>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        id="footerInviteSent"
                        name="footerInviteSent"
                        checked={employeeData.inviteSent || false}
                        onChange={(e) => {
                          setEmployeeData((prev) => ({
                            ...prev,
                            inviteSent: e.target.checked,
                          }));
                        }}
                        className="mr-2 rounded border-0 text-redhalo-navy shadow-sm focus:ring focus:ring-redhalo-red/25 focus:ring-opacity-50"
                        disabled={!employeeData.essEnabled || isActiveTabLocked || loading}
                      />
                      <span
                        className={`text-sm font-medium ${
                          darkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Send Invite
                      </span>
                    </label>
                    {!employeeData.essEnabled && (
                      <span
                        className={`text-xs ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Enable ESS to activate invite actions.
                      </span>
                    )}
                    {employeeData.welcomeEmailSent && !(employeeData.user?.lastLoginAt || employee?.user?.lastLoginAt) && (
                      <span
                        className={`text-sm font-medium ${
                          darkMode ? "text-emerald-400" : "text-emerald-700"
                        }`}
                      >
                        Email Already Sent
                      </span>
                    )}
                  </>
                  {(employeeData.user?.lastLoginAt || employee?.user?.lastLoginAt) && (
                    <span
                      className={`text-sm ${
                        darkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                      title="Last login with official email"
                    >
                      Employee last logged:{" "}
                      {new Date(
                        employeeData.user?.lastLoginAt || employee?.user?.lastLoginAt
                      ).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </span>
                  )}
                </>
              )}
            </div>

            {/* Right side - Action buttons */}
            <div className="flex justify-end space-x-2">
              {!isFirstTab() && (
                <button
                  type="button"
                  onClick={goToPreviousTab}
                  className={`rh-btn-secondary rounded-md px-4 py-2 text-sm font-semibold transition-colors hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800 ${
                    loading ? "pointer-events-none opacity-50" : ""
                  }`}
                  disabled={loading}
                >
                  Previous
                </button>
              )}
              <button
                type="button"
                onClick={onCancel}
                className={
                  isEmployeeView
                    ? `rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/35 focus-visible:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 dark:focus-visible:ring-offset-gray-900 ${
                        loading ? "pointer-events-none opacity-50" : ""
                      }`
                    : `rounded-md px-4 py-2 text-sm font-medium text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800 ${
                        darkMode
                          ? "bg-red-600 hover:bg-red-700"
                          : "bg-red-500 hover:bg-red-600"
                      } ${loading ? "pointer-events-none opacity-50" : ""}`
                }
                disabled={loading}
              >
                {isEmployeeView ? "Close" : "Cancel"}
              </button>
              {!isLastTab() ? (
                <button
                  onClick={async () => {
                    try {
                      setLoading(true);
                      if (forceReadOnly) {
                        toast.error("This employee record is locked and cannot be edited.", {
                          position: "top-right",
                        });
                        return;
                      }
                      if (activeTabPermissionReadOnly) {
                        toast.error("You do not have permission to edit this profile tab.", {
                          position: "top-right",
                        });
                        return;
                      }
                      // If this tab is locked, switch to edit mode instead of saving.
                      if (!isNewEmployee && isActiveTabLocked) {
                        setEditModeByTab((prev) => ({ ...prev, [activeTab]: true }));
                        return;
                      }
                      // For new employee (onboard), always use org save so rejoin API is called correctly from whichever tab
                      if (isNewEmployee && activeTab !== "org") {
                        const orgHandler = formSaveHandlersRef.current["org"];
                        if (typeof orgHandler === "function") {
                          const success = await orgHandler();
                          if (success && !isNewEmployee) {
                            setEditModeByTab((prev) => ({ ...prev, [activeTab]: false }));
                          }
                        } else {
                          toast.error("Please complete Organization Details and onboard from the Org Details tab.");
                        }
                        return;
                      }
                      // Call the form's handleSave which handles validation, save, and navigation automatically
                      // Form's handleSave will call onNext() after successful save if not last tab
                      const saveResult = await triggerFormSave();
                      const saveSuccess = saveResult === true || (saveResult && saveResult.success);

                      // Onboard/rejoin just succeeded: close modal and refresh list
                      if (saveResult && (saveResult.created || (saveResult.success && isNewEmployee))) {
                        if (typeof onOnboardSuccess === "function") onOnboardSuccess();
                        return;
                      }

                      // After successful save, lock the tab again (non-org tabs only)
                      if (saveSuccess && !isNewEmployee) {
                        setEditModeByTab((prev) => ({ ...prev, [activeTab]: false }));
                      }
                    } catch (error) {
                      console.error("Error saving:", error);
                      // Form's handleSave already handles errors and prevents navigation on error
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className={
                    loading
                      ? "px-4 py-2 rounded-none text-sm font-medium transition-colors bg-gray-400 text-gray-600 cursor-not-allowed"
                      : forceReadOnly
                      ? "px-4 py-2 rounded-none text-sm font-medium transition-colors bg-gray-300 text-gray-500 cursor-not-allowed"
                      : (!isNewEmployee && isActiveTabLocked)
                      ? "rh-btn-primary rounded-md px-4 py-2 text-sm font-medium transition-colors hover:opacity-95"
                      : darkMode
                      ? "px-4 py-2 rounded-none text-sm font-medium transition-colors rh-btn-primary hover:opacity-95"
                      : "px-4 py-2 rounded-none text-sm font-medium transition-colors rh-btn-primary"
                  }
                  disabled={loading || forceReadOnly}
                >
                  {(!isNewEmployee && isActiveTabLocked)
                    ? (forceReadOnly ? "Locked" : "Edit")
                    : (isNewEmployee
                      ? loading
                        ? "Onboarding..."
                        : "Onboard Employee"
                      : (isEmployeeView && activeTab === "org"
                        ? "Complete My Profile"
                        : (isEmployeeView ? "Update & Next" : "Update")))}
                </button>
              ) : (
                <>
                  {/* Documents tab: show a separate Edit button that is NOT blocked by Update's disabled state */}
                  {!forceReadOnly && !isNewEmployee && activeTab === "documents" && isActiveTabLocked && (
                    <button
                      onClick={() =>
                        setEditModeByTab((prev) => ({ ...prev, [activeTab]: true }))
                      }
                      className={
                        loading
                          ? "px-4 py-2 rounded-none text-sm font-medium transition-colors bg-gray-400 text-gray-600 cursor-not-allowed"
                          : "rh-btn-primary rounded-md px-4 py-2 text-sm font-medium transition-colors hover:opacity-95"
                      }
                      disabled={loading}
                    >
                      Edit
                    </button>
                  )}

                  <span
                    className="inline-block"
                    title={
                      activeTab === "documents" &&
                      !documentsFinalizeState?.canFinalize
                        ? "Please complete all required fields in previous tabs and upload all required documents"
                        : ""
                    }
                    onClick={async () => {
                    if (forceReadOnly) {
                      toast.error("This employee record is locked and cannot be edited.", {
                        position: "top-right",
                      });
                      return;
                    }
                    if (activeTabPermissionReadOnly) {
                      toast.error("You do not have permission to edit this profile tab.", {
                        position: "top-right",
                      });
                      return;
                    }
                    // If this tab is locked, switch to edit mode instead of saving.
                    if (!isNewEmployee && activeTab !== "org" && isActiveTabLocked) {
                      setEditModeByTab((prev) => ({ ...prev, [activeTab]: true }));
                      return;
                    }

                    // For new employee (onboard), always use org save so rejoin API is called correctly from whichever tab
                    if (isNewEmployee) {
                      const orgHandler = formSaveHandlersRef.current["org"];
                      if (typeof orgHandler === "function") {
                        try {
                          setLoading(true);
                          const result = await orgHandler();
                          if (result && (result.created || result.saved)) {
                            if (typeof onOnboardSuccess === "function") onOnboardSuccess();
                          }
                        } finally {
                          setLoading(false);
                        }
                      } else {
                        toast.error("Please complete Organization Details and onboard from the Org Details tab.");
                      }
                      return;
                    }

                    // If Documents tab isn't ready, call its exposed save handler to show toast messages.
                    if (
                      activeTab === "documents" &&
                      !documentsFinalizeState?.canFinalize
                    ) {
                      const docsHandler =
                        formSaveHandlersRef.current["documents"];
                      if (typeof docsHandler === "function") {
                        await docsHandler();
                      } else {
                        toast.error(
                          "Please complete all required fields and upload required documents before saving.",
                          { position: "top-right" }
                        );
                      }
                      return;
                    }

                    try {
                      setLoading(true);
                      // Call the form's handleSave which handles validation and save
                      const saveSuccess = await triggerFormSave();
                      if (saveSuccess && !isNewEmployee && activeTab !== "org") {
                        setEditModeByTab((prev) => ({ ...prev, [activeTab]: false }));
                      }
                    } catch (error) {
                      console.error("Error saving:", error);
                      // Form's handleSave already handles errors
                    } finally {
                      setLoading(false);
                    }
                  }}
                  >
                    <button
                      className={
                        loading ||
                        forceReadOnly ||
                        activeTabPermissionReadOnly ||
                        (activeTab === "documents" &&
                          !documentsFinalizeState?.canFinalize)
                          ? "px-4 py-2 rounded-none text-sm font-medium transition-colors bg-gray-400 text-gray-600 cursor-not-allowed"
                          : darkMode
                          ? "rh-btn-primary rounded-md px-4 py-2 text-sm font-medium transition-colors hover:opacity-95"
                          : "rh-btn-primary rounded-md px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90"
                      }
                      disabled={
                        loading ||
                        forceReadOnly ||
                        activeTabPermissionReadOnly ||
                        (activeTab === "documents" &&
                          !documentsFinalizeState?.canFinalize)
                      }
                    >
                      {isNewEmployee ? "Save" : "Update Employee"}
                    </button>
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ProofDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleDocumentUpload}
        onCancel={onCancel}
        employeeData={employeeData}
        isPfApplicable={employeeData.pfApplicable}
        isEsiApplicable={employeeData.persistedEsiApplicable}
        canDownloadPrefilledDocuments={documentsFinalizeState?.tabsValid === true}
      />

      {/* Document Replace Confirmation Modal */}
      {documentReplaceModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
                <h3 className="text-xl font-semibold text-gray-800">
                  Replace Document?
                </h3>
              </div>
              <button
                onClick={() => handleDocumentReplaceConfirm(false)}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100 transition-colors duration-200"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-gray-700 mb-4">
                A document <span className="font-semibold">"{documentReplaceModal.documentName}"</span> already exists.
              </p>
              
              {documentReplaceModal.existingDoc && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-3 border border-gray-200">
                  <div className="flex items-start space-x-3">
                    <FileText className="w-5 h-5 text-gray-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">File Name</p>
                      <p className="text-sm text-gray-800">{documentReplaceModal.existingDoc.fileName || documentReplaceModal.existingDoc.originalFileName || "N/A"}</p>
                    </div>
                  </div>
                  
                  {documentReplaceModal.existingDoc.size && (
                    <div className="flex items-start space-x-3">
                      <HardDrive className="w-5 h-5 text-gray-500 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-600">Size</p>
                        <p className="text-sm text-gray-800">
                          {documentReplaceModal.existingDoc.size > 1024
                            ? (documentReplaceModal.existingDoc.size / 1024).toFixed(1) + " KB"
                            : documentReplaceModal.existingDoc.size + " bytes"}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {documentReplaceModal.existingDoc.lastUpdated && (
                    <div className="flex items-start space-x-3">
                      <Calendar className="w-5 h-5 text-gray-500 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-600">Uploaded</p>
                        <p className="text-sm text-gray-800">
                          {formatDate(documentReplaceModal.existingDoc.lastUpdated)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <p className="text-gray-600 text-sm mt-4">
                Do you want to replace the existing document with the new one?
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => handleDocumentReplaceConfirm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDocumentReplaceConfirm(true)}
                className="px-4 py-2 rh-btn-primary rounded-lg hover:opacity-95 transition-colors duration-200 font-medium"
              >
                Replace
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
